import { Text } from '@southfields-digital/mpxlive-components';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import Playlist, { IPlaylistProps } from 'src/components/Playlist/Playlist';
import useKeypress from 'src/hooks/useKeypress';
import { StateType } from 'src/redux/reducers';
import {
  REQUEST_DELETE_PLAYLIST,
  REQUEST_UPDATE_PLAYLIST,
  REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
  PlaylistGetAllStateType,
  REQUEST_SET_SELECTED_PLAYLIST,
  REQUEST_REORDER_PLAYLISTS,
  REQUEST_DUPLICATE_PLAYLIST,
  REQUEST_STOP_AUTO_PLAY_PLAYLIST,
  REQUEST_START_AUTO_PLAY_PLAYLIST,
} from 'src/redux/reducers/playlist';
import {
  PlaylistGraphicStateType,
  REQUEST_MOVE_PLAYLIST_GRAPHIC,
} from 'src/redux/reducers/playlistGraphic';

import { DND_ITEM_TYPES, DndKitSortableList, DragItem } from '../DndKitSortableList';
import Select, { Option } from '../Select';

import styles from './Playlists.module.scss';


type PlaylistsProps = {
  rundownId: string;
  view?: 'select';
};

export default function Playlists({ rundownId, view }: PlaylistsProps) {
  const dispatch = useDispatch();
  const { data: playlists, selectedId }: PlaylistGetAllStateType = useSelector(
    (state: StateType) => state.playlist.getAll
  );
  const { playlistGraphics }: PlaylistGraphicStateType = useSelector(
    (state: StateType) => state.playlistGraphic
  );

  useEffect(() => {
    dispatch({
      type: REQUEST_GET_ALL_PLAYLISTS_BY_RUNDOWN_ID,
      payload: { rundownId, firstSelected: true },
    });
  }, [dispatch, rundownId]);

  const [editPlaylistId, setEditPlaylistId] = useState<string | null>(null);

  const handleSubmitEdit = (playlistName: string) => {
    if (editPlaylistId) {
      dispatch({
        type: REQUEST_UPDATE_PLAYLIST,
        payload: { id: editPlaylistId, name: playlistName, rundownId },
      });
    }
    setEditPlaylistId(null);
  };

  const handleUpdatePlaylist = (
    playlistId: string,
    data: { name?: string; autoplay?: AutoPlay }
  ) => {
    dispatch({
      type: REQUEST_UPDATE_PLAYLIST,
      payload: { id: playlistId, ...data },
    });
  };

  const handleClickDelete = (playlistId: string) => {
    dispatch({ type: REQUEST_DELETE_PLAYLIST, payload: { id: playlistId } });
  };

  const handleClickPlaylist = (playlistId: string) => {
    dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
  };

  const handleSortPlaylists = (sortedValues: IPlaylistProps[]) => {
    const sortedPlaylists: string[] = sortedValues.map((item) => item.id);
    dispatch({
      type: REQUEST_REORDER_PLAYLISTS,
      payload: { playlistIds: sortedPlaylists, rundownId },
    });
  };

  const handleDuplicatePlaylist = (playlistId: string) => {
    dispatch({ type: REQUEST_DUPLICATE_PLAYLIST, payload: { id: playlistId, rundownId } });
  };

  const handleToggleAutoPlayPlaylist = (playlist: Playlist) => {
    dispatch({
      type: playlist.autoplay.isRunning
        ? REQUEST_STOP_AUTO_PLAY_PLAYLIST
        : REQUEST_START_AUTO_PLAY_PLAYLIST,
      payload: {
        playlistId: playlist.id,
      },
    });
  };

  const previousPlaylist = () => {
    if (!playlists) return;

    const index = playlists.findIndex((playlist) => playlist.id === selectedId);
    if (index > 0) {
      const playlistId = playlists[index - 1].id;
      dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
    }
  };

  const nextPlaylist = () => {
    if (!playlists) return;

    const index = playlists.findIndex((playlist) => playlist.id === selectedId);
    if (index < playlists.length - 1) {
      const playlistId = playlists[index + 1].id;
      dispatch({ type: REQUEST_SET_SELECTED_PLAYLIST, payload: { id: playlistId } });
    }
  };

  useKeypress('PageUp', { shiftKey: true }, previousPlaylist);
  useKeypress('PageDown', { shiftKey: true }, nextPlaylist);

  const canAcceptDrop = (activeData: DragItem, overData: DragItem): boolean =>
    activeData.itemType === DND_ITEM_TYPES.GRAPHIC && overData.itemType === DND_ITEM_TYPES.PLAYLIST;

  const handleDrop = (activeData: DragItem, overData: DragItem): boolean => {
    if (
      activeData.itemType !== DND_ITEM_TYPES.GRAPHIC ||
      overData.itemType !== DND_ITEM_TYPES.PLAYLIST
    ) {
      return false;
    }

    const playlistGraphicId = activeData.props.id;
    const targetPlaylistId = overData.props.id;

    const playlistGraphic = playlistGraphics?.find((pg) => pg.id === playlistGraphicId);

    if (!playlistGraphic) {
      return false;
    }

    const currentPlaylistId = playlistGraphic.playlist.id;

    if (currentPlaylistId === targetPlaylistId) {
      return false;
    }

    dispatch({
      type: REQUEST_MOVE_PLAYLIST_GRAPHIC,
      payload: {
        playlistGraphicId,
        targetPlaylistId,
      },
    });

    return true;
  };

  const items = useMemo<IPlaylistProps[]>(
    () =>
      playlists?.map((playlist) => ({
        id: playlist.id,
        name: playlist.name,
        autoplay: playlist.autoplay,
        active: selectedId === playlist.id,
        isOpen: playlist.id === editPlaylistId,
        numberGraphics:
          playlistGraphics?.filter((graphic) => graphic.playlist.id === playlist.id).length || 0,
        onUpdatePlaylist: (autoplay: AutoPlay) => handleUpdatePlaylist(playlist.id, { autoplay }),
        onClickAutoPlay: () => handleToggleAutoPlayPlaylist(playlist),
        onClick: () => handleClickPlaylist(playlist.id),
        onEdit: () => setEditPlaylistId(playlist.id),
        onCopy: () => handleDuplicatePlaylist(playlist.id),
        onDelete: () => handleClickDelete(playlist.id),
        onClose: () => setEditPlaylistId(null),
        onSubmit: (name: string) => handleSubmitEdit(name),
      })) || [],
    [
      playlists,
      selectedId,
      editPlaylistId,
      playlistGraphics,
      handleUpdatePlaylist,
      handleToggleAutoPlayPlaylist,
      handleClickPlaylist,
      handleDuplicatePlaylist,
      handleClickDelete,
      handleSubmitEdit,
    ]
  );

  if (!playlists?.length) {
    return (
      <div className={styles.NoContent}>
        <Text as="p">No playlists</Text>
      </div>
    );
  }

  if (view === 'select') {
    const playlistOptions: Option[] =
      playlists?.map(({ id, name }) => ({ label: name, value: id })) || [];

    return (
      <div className={classNames(styles.Container, 'p-6')}>
        <Select
          onChange={(e) => handleClickPlaylist(e.target.value)}
          options={playlistOptions}
          value={selectedId || ''}
        />
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <DndKitSortableList
        items={items}
        setItems={handleSortPlaylists}
        activeId={selectedId || undefined}
        Component={Playlist}
        itemType={DND_ITEM_TYPES.PLAYLIST}
        canAcceptDrop={canAcceptDrop}
        onDrop={handleDrop}
      />
    </div>
  );
}
